import {
  Box,
  Divider,
  Flex,
  Grid,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Link, useParams } from "react-router-dom";
import {
  useCreateUserWithUserGroupIdMutation,
  useUserGroupQuery,
} from "./index.generated";
import { AdminSidebar } from "../_shared/Sidebar";
import { CourseIcon } from "../../_shared/CourseIcon";
import { UserIcon } from "../../_shared/UserIcon";
import { BlueButton } from "../../_shared/Buttons";
import { useEffect, useMemo, useState } from "react";
import { SearchIcon } from "@chakra-ui/icons";

export const AdminUserGroupTop = () => {
  const params = useParams<{ userGroupId: string }>();
  const userGroupId = params.userGroupId!;

  const [res] = useUserGroupQuery({
    variables: { input: userGroupId },
  });

  if (res.fetching) return <Text>loading...</Text>;

  if (!res.data?.userGroup) return null;
  const userGroup = res.data.userGroup;
  const courses = res.data.userGroup.courses;
  const users = res.data.userGroup.users;

  const isActiveRouteChangeAlertFlag = useMemo(() => {
    return res.data?.appConfig.routeChangeAlert;
  }, [res.data.appConfig.routeChangeAlert]);

  courses.sort((a, b) => {
    if (a.updatedAt > b.updatedAt) return -1;
    else {
      return 1;
    }
  });

  const [searchedWord, setSearchedWord] = useState("");
  const [filteredCourses, setFilteredCourses] = useState(courses || []);

  useEffect(() => {
    if (searchedWord === "") {
      setFilteredCourses(courses);
      return;
    }
    const filtered = courses.filter((course) => {
      return course.name.includes(searchedWord);
    });
    setFilteredCourses(filtered);
  }, [searchedWord, res]);

  return (
    <Flex>
      <AdminSidebar />
      <Box bgColor={"#16181a"} flexGrow={1} p={10}>
        <Flex py={2} my={4}>
          <Link to={`/admin`}>
            <Text fontSize={40} color="white" fontWeight={"bold"} mb={8}>
              特約店一覧
            </Text>
          </Link>
          <Text fontSize={40} color="white" fontWeight={"bold"} mb={8}>
            {`>`}
            {userGroup.name}
          </Text>
        </Flex>
        <Flex
          flexDir="row"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          py={2}
          my={4}
        >
          <Text textColor="white" fontSize={30} fontWeight="bold" mb={2}>
            コース一覧
          </Text>
          <InputGroup
            maxW={300}
            w={"full"}
            display={{ base: "none", md: "flex" }}
          >
            <Input
              placeholder="検索"
              _placeholder={{ color: "gray.500" }}
              onChange={(event) => {
                setSearchedWord(event.target.value);
              }}
              maxW={300}
              textColor="white"
            />
            <InputRightElement
              pointerEvents="none"
              children={<SearchIcon color="gray.500" />}
            />
          </InputGroup>
        </Flex>
        {filteredCourses.length === 0 ? (
          <Text fontSize={20} fontWeight="bold" textColor={"white"}>
            該当するコースが存在しません。
          </Text>
        ) : (
          <Grid templateColumns="repeat(5, 1fr)" gap={8} mb={4}>
            {filteredCourses.map((course) => {
              return (
                <CourseIcon
                  key={course.id}
                  course={course}
                  isViewMode={false}
                  isActiveRouteChangeAlertFlag={isActiveRouteChangeAlertFlag}
                />
              );
            })}
          </Grid>
        )}
        <Divider border={2} color="white" my={8} />
        <Flex
          flexDir="row"
          flexWrap="wrap"
          alignContent="center"
          justifyContent="space-between"
          py={2}
          my={4}
        >
          <Text textColor="white" fontSize={30} fontWeight="bold" mb={2}>
            ユーザー一覧
          </Text>
          <Flex gap={2} justifyItems="center">
            <CreateUserWithUserGroupIdModal
              userGroupId={userGroup.id}
              userGroupName={userGroup.name}
            />
          </Flex>
        </Flex>
        <Grid templateColumns="repeat(5, 1fr)" gap={8} mb={4}>
          {users.map((user) => {
            return <UserIcon key={user.id} user={user} />;
          })}
        </Grid>
      </Box>
    </Flex>
  );
};

const CreateUserWithUserGroupIdModal = ({
  userGroupId,
  userGroupName,
}: {
  userGroupId: string;
  userGroupName: string;
}) => {
  const [createUserRes, createUserWithUserGroup] =
    useCreateUserWithUserGroupIdMutation();

  const toast = useToast();

  const [name, setName] = useState("");
  const [isValidName, setIsValidName] = useState(false);
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (name.length === 0) {
      setIsValidName(false);
    } else if (name.includes(" ")) {
      setIsValidName(false);
    } else if (name.includes("　")) {
      setIsValidName(false);
    } else {
      setIsValidName(true);
    }
  }, [name]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isOpenPasswordModal,
    onOpen: onOpenPasswordModal,
    onClose: onClosePasswordModal,
  } = useDisclosure();

  useEffect(() => {
    if (createUserRes.data?.createUserWithUserGroupId.password) {
      onOpenPasswordModal();
      setPassword(createUserRes.data.createUserWithUserGroupId.password);
    }
    if (createUserRes.data?.createUserWithUserGroupId.error) {
      toast({
        title: "ユーザーの作成に失敗しました。",
        description: createUserRes.data?.createUserWithUserGroupId.error,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [createUserRes.data]);

  return (
    <>
      <BlueButton onClick={onOpen} label={"特約店ユーザーを作成"} />
      <Modal isOpen={isOpen} onClose={onClose} size={"xl"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>特約店ユーザーを作成</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text pb={2} fontSize={20}>
              下記特約店のユーザーを作成します。
            </Text>
            <Text pb={2} fontSize={20}>
              特約店名 : {userGroupName}
            </Text>
            <Input
              placeholder={"名前"}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Flex>
              <BlueButton
                isDisabled={!isValidName}
                onClick={() => {
                  createUserWithUserGroup(
                    {
                      input: {
                        name,
                        userGroupId,
                      },
                    },
                    { additionalTypenames: ["User"] }
                  );
                  onClose();
                }}
                label={"作成"}
              />
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ShowPasswordModal
        isOpen={isOpenPasswordModal}
        onClose={onClosePasswordModal}
        name={name}
        password={password}
      />
    </>
  );
};

const ShowPasswordModal = ({
  isOpen,
  onClose,
  name,
  password,
}: {
  isOpen: boolean;
  onClose: () => void;
  name: string | undefined;
  password: string | undefined;
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"xl"}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>ユーザーを作成しました。</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text pb={2} fontSize={20}>
            ユーザーを作成しました。
          </Text>
          <Text pb={2} fontSize={20} color="red.900">
            以下のパスワードは一度しか表示されません。
          </Text>
          <Text pb={2} fontSize={20}>
            必ずメモしてください。
          </Text>

          <Text pb={2} fontSize={20}>
            ユーザー名 : {name}
          </Text>
          <Text pb={2} fontSize={20}>
            パスワード : {password}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Flex>
            <BlueButton onClick={onClose} label={"ウィンドウを閉じる"} />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
